import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Root of a sheet (controls open state). It traps focus and closes on Escape or a scrim click, like `Dialog`. */
export const Sheet = DialogPrimitive.Root

const SIDES = {
  left: 'left-0 w-72 max-w-[85vw] border-r data-[state=closed]:animate-sheet-out-left data-[state=open]:animate-sheet-in-left',
  right:
    'right-0 w-full border-l sm:max-w-2xl data-[state=closed]:animate-sheet-out-right data-[state=open]:animate-sheet-in-right',
} as const

interface SheetContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  /** `left` for mobile navigation; `right` for a record's details over a list (use `RecordSheet`). */
  side: keyof typeof SIDES
  /** Accessible name of the close button. */
  closeLabel?: string
}

/** A full-height panel that slides in from one edge over a scrim. */
export function SheetContent({ ref, side, closeLabel = 'Close', className, children, ...props }: SheetContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-scrim-out data-[state=open]:animate-scrim-in" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-y-0 z-50 flex flex-col border-border bg-card text-card-foreground shadow-overlay outline-none',
          SIDES[side],
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute top-3 right-3 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
          <X className="size-4" />
          <span className="sr-only">{closeLabel}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

/** The sheet's top bar: a hairline-bottomed 48px row that leaves room for the close button. */
export function SheetHeader({ ref, className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      ref={ref}
      className={cn('flex min-h-12 shrink-0 items-center gap-2 border-b border-border py-2 pr-12 pl-card', className)}
      {...props}
    />
  )
}

/** The accessible title of the sheet. */
export function SheetTitle({ ref, className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('truncate text-title leading-tight font-semibold text-foreground', className)}
      {...props}
    />
  )
}

/** The accessible description of the sheet. */
export function SheetDescription({ ref, className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description ref={ref} className={cn('text-caption text-muted-foreground', className)} {...props} />
}

/** The sheet's content column; it scrolls on its own so the header stays put. */
export function SheetBody({ ref, className, ...props }: React.ComponentProps<'div'>) {
  return <div ref={ref} className={cn('min-h-0 flex-1 overflow-y-auto', className)} {...props} />
}
